import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
 ChevronRight,
 Loader2,
 AlertCircle,
 BarChart3,
 Target,
 Clock,
 CheckCircle,
 TrendingUp,
 BookOpen,
 AlertTriangle,
 X,
} from 'lucide-react';
import { lldAnalyticsService, LLDAnalytics, LLDRecommendation } from '../../../services/lldAnalyticsService';

const DIMENSION_LABELS: Record<string, string> = {
 requirementsCoverage: 'Requirements Coverage',
 entityModeling: 'Entity Modeling',
 relationships: 'Relationships',
 umlDesign: 'UML Design',
 codeQuality: 'Code Quality',
 completeness: 'Completeness',
};

const DIMENSION_KEYS = [
 'requirementsCoverage',
 'entityModeling',
 'relationships',
 'umlDesign',
 'codeQuality',
 'completeness',
];

function getScoreColor(score: number): string {
 if (score >= 70) return 'bg-green-500';
 if (score >= 50) return 'bg-yellow-500';
 return 'bg-red-500';
}

function getScoreTextColor(score: number): string {
 if (score >= 70) return 'text-green-600 dark:text-green-400';
 if (score >= 50) return 'text-yellow-600 dark:text-yellow-400';
 return 'text-red-600 dark:text-red-400';
}

function getWeakAreaIndicatorColor(score: number): string {
 if (score < 30) return 'border-red-500 bg-red-50 dark:bg-red-900/20';
 if (score < 50) return 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
 return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
}

/**
 * Simple SVG-based line chart for progress trends.
 * Renders average score per week when 2+ data points exist.
 */
const ProgressTrendChart: React.FC<{ weeklyTrends: LLDAnalytics['weeklyTrends'] }> = ({ weeklyTrends }) => {
 if (weeklyTrends.length < 2) {
  return (
   <div className="flex items-center justify-center h-48 text-gray-600 dark:text-gray-400 text-sm">
    <p>Complete at least 2 problems to see progress trends.</p>
   </div>
  );
 }

 const chartWidth = 600;
 const chartHeight = 200;
 const padding = { top: 20, right: 20, bottom: 40, left: 50 };
 const innerWidth = chartWidth - padding.left - padding.right;
 const innerHeight = chartHeight - padding.top - padding.bottom;

 const maxScore = 100;
 const minScore = 0;

 const xStep = innerWidth / (weeklyTrends.length - 1);

 const points = weeklyTrends.map((trend, index) => ({
  x: padding.left + index * xStep,
  y: padding.top + innerHeight - ((trend.averageScore - minScore) / (maxScore - minScore)) * innerHeight,
  label: trend.week,
  score: trend.averageScore,
 }));

 const pathData = points
  .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
  .join(' ');

 // Area fill
 const areaPath = `${pathData} L ${points[points.length - 1].x} ${padding.top + innerHeight} L ${points[0].x} ${padding.top + innerHeight} Z`;

 // Y-axis labels
 const yLabels = [0, 25, 50, 75, 100];

 return (
  <div className="w-full overflow-x-auto">
   <svg
    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
    className="w-full min-w-[400px] h-auto"
    preserveAspectRatio="xMidYMid meet"
   >
    {/* Grid lines */}
    {yLabels.map((label) => {
     const y = padding.top + innerHeight - (label / maxScore) * innerHeight;
     return (
      <g key={label}>
       <line
        x1={padding.left}
        y1={y}
        x2={padding.left + innerWidth}
        y2={y}
        stroke="currentColor"
        strokeOpacity={0.1}
        strokeDasharray="4 4"
       />
       <text
        x={padding.left - 8}
        y={y + 4}
        textAnchor="end"
        className="fill-gray-500 dark:fill-gray-400"
        fontSize="10"
       >
        {label}%
       </text>
      </g>
     );
    })}

    {/* Area fill */}
    <path d={areaPath} fill="url(#chartGradient)" opacity={0.3} />

    {/* Line */}
    <path d={pathData} fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

    {/* Data points */}
    {points.map((point, index) => (
     <g key={index}>
      <circle cx={point.x} cy={point.y} r="4" fill="#8b5cf6" stroke="white" strokeWidth="2" />
      {/* X-axis label */}
      <text
       x={point.x}
       y={padding.top + innerHeight + 20}
       textAnchor="middle"
       className="fill-gray-500 dark:fill-gray-400"
       fontSize="9"
      >
       {point.label}
      </text>
     </g>
    ))}

    {/* Gradient definition */}
    <defs>
     <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.4" />
      <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
     </linearGradient>
    </defs>
   </svg>
  </div>
 );
};

/**
 * Dimension score bar component showing visual bar with percentage.
 */
const DimensionScoreBar: React.FC<{
 dimension: string;
 score: number;
 isWeak: boolean;
 onClick: () => void;
}> = ({ dimension, score, isWeak, onClick }) => {
 const percentage = Math.round(score * 10); // Convert 0-10 to 0-100

 return (
  <button
   onClick={onClick}
   className={`w-full text-left p-3 rounded-lg border transition-all hover:shadow-sm ${isWeak
     ? getWeakAreaIndicatorColor(percentage)
     : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
    }`}
  >
   <div className="flex items-center justify-between mb-2">
    <div className="flex items-center gap-2">
     {isWeak && <AlertTriangle className="w-4 h-4 text-orange-500" />}
     <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
      {DIMENSION_LABELS[dimension] || dimension}
     </span>
    </div>
    <span className={`text-sm font-semibold ${getScoreTextColor(percentage)}`}>
     {percentage}%
    </span>
   </div>
   <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
    <div
     className={`h-2.5 rounded-full transition-all duration-500 ${getScoreColor(percentage)}`}
     style={{ width: `${percentage}%` }}
    />
   </div>
  </button>
 );
};

const AnalyticsDashboard: React.FC = () => {
 const [analytics, setAnalytics] = useState<LLDAnalytics | null>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [selectedWeakArea, setSelectedWeakArea] = useState<string | null>(null);
 const [recommendations, setRecommendations] = useState<LLDRecommendation[]>([]);
 const [loadingRecommendations, setLoadingRecommendations] = useState(false);

 useEffect(() => {
  const fetchAnalytics = async () => {
   setLoading(true);
   setError(null);
   try {
    const data = await lldAnalyticsService.getAnalytics();
    setAnalytics(data);
   } catch (err: any) {
    console.error('Failed to fetch analytics:', err);
    setError(err.message || 'Failed to load analytics. Please try again.');
   } finally {
    setLoading(false);
   }
  };

  fetchAnalytics();
 }, []);

 const handleWeakAreaClick = async (dimension: string) => {
  if (selectedWeakArea === dimension) {
   setSelectedWeakArea(null);
   setRecommendations([]);
   return;
  }

  setSelectedWeakArea(dimension);
  setLoadingRecommendations(true);
  try {
   const recs = await lldAnalyticsService.getRecommendations(dimension);
   setRecommendations(recs);
  } catch (err: any) {
   console.error('Failed to fetch recommendations:', err);
   setRecommendations([]);
  } finally {
   setLoadingRecommendations(false);
  }
 };

 // Compute weak areas from analytics data
 const weakAreaDimensions = analytics?.weakAreas?.map((w) => w.dimension) ?? [];

 // Compute average time in minutes from recent evaluations or totalCompleted
 const averageTimeMinutes = analytics?.recentEvaluations?.length
  ? '—'
  : '—';

 if (loading) {
  return (
   <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
     <div className="flex flex-col items-center justify-center py-24">
      <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-4" />
      <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
     </div>
    </div>
   </div>
  );
 }

 if (error) {
  return (
   <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
     <div className="flex flex-col items-center justify-center py-24">
      <AlertCircle className="w-10 h-10 text-red-500 mb-4" />
      <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
      <button
       onClick={() => window.location.reload()}
       className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
      >
       Retry
      </button>
     </div>
    </div>
   </div>
  );
 }

 // Empty state: no completed problems
 if (!analytics || analytics.totalCompleted === 0) {
  return (
   <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
     {/* Breadcrumb */}
     <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-6">
      <Link to="/practice" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
       Practice
      </Link>
      <ChevronRight className="w-4 h-4 mx-2" />
      <Link to="/practice/lld" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
       LLD Practice
      </Link>
      <ChevronRight className="w-4 h-4 mx-2" />
      <span className="text-gray-900 dark:text-gray-100 font-medium">Analytics</span>
     </nav>

     <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8 font-display">
      LLD Analytics Dashboard
     </h1>

     <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <BarChart3 className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
      <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No analytics data yet</p>
      <p className="text-gray-500 dark:text-gray-500 text-sm mb-6 text-center max-w-md">
       Statistics will appear after completing at least one problem. Start practicing to track your progress!
      </p>
      <Link
       to="/practice/lld"
       className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
      >
       <BookOpen className="w-4 h-4" />
       Browse Problem Catalog
      </Link>
     </div>
    </div>
   </div>
  );
 }

 // Calculate the number of attempted problems from recent evaluations
 const attemptedCount = analytics.recentEvaluations?.length ?? analytics.totalCompleted;

 return (
  <div className="lld-page lld-analytics-page min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
   <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    {/* Breadcrumb */}
    <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-6">
     <Link to="/practice" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
      Practice
     </Link>
     <ChevronRight className="w-4 h-4 mx-2" />
     <Link to="/practice/lld" className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
      LLD Practice
     </Link>
     <ChevronRight className="w-4 h-4 mx-2" />
     <span className="text-gray-900 dark:text-gray-100 font-medium">Analytics</span>
    </nav>

    {/* Header */}
    <div className="flex items-center gap-3 mb-8">
     <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600">
      <BarChart3 className="w-6 h-6 text-white" />
     </div>
     <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-display">
       LLD Analytics Dashboard
      </h1>
      <p className="text-gray-600 dark:text-gray-400">
       Track your progress and identify areas for improvement
      </p>
     </div>
    </div>

    {/* Overall Statistics Cards */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
     {/* Attempted */}
     <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center gap-3 mb-2">
       <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
        <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
       </div>
       <span className="text-sm text-gray-600 dark:text-gray-400">Attempted</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{attemptedCount}</p>
     </div>

     {/* Completed */}
     <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center gap-3 mb-2">
       <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
       </div>
       <span className="text-sm text-gray-600 dark:text-gray-400">Completed</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{analytics.totalCompleted}</p>
     </div>

     {/* Average Score */}
     <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center gap-3 mb-2">
       <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
        <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
       </div>
       <span className="text-sm text-gray-600 dark:text-gray-400">Avg Score</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
       {Math.round(analytics.averageScore * 10)}%
      </p>
     </div>

     {/* Average Time */}
     <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center gap-3 mb-2">
       <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
        <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
       </div>
       <span className="text-sm text-gray-600 dark:text-gray-400">Avg Time</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{averageTimeMinutes}</p>
     </div>
    </div>

    {/* Progress Trend Chart */}
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
     <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
      <TrendingUp className="w-5 h-5 text-purple-500" />
      Progress Trends
     </h2>
     <ProgressTrendChart weeklyTrends={analytics.weeklyTrends} />
    </div>

    {/* Dimension Score Breakdown */}
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
     <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
      <BarChart3 className="w-5 h-5 text-purple-500" />
      Dimension Scores
     </h2>
     <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
      Click on a weak area (marked with ⚠) to see recommended problems.
     </p>
     <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {DIMENSION_KEYS.map((dimension) => {
       const score = analytics.dimensionAverages[dimension] ?? 0;
       const isWeak = weakAreaDimensions.includes(dimension);

       return (
        <DimensionScoreBar
         key={dimension}
         dimension={dimension}
         score={score}
         isWeak={isWeak}
         onClick={() => isWeak ? handleWeakAreaClick(dimension) : undefined}
        />
       );
      })}
     </div>
    </div>

    {/* Weak Areas Section */}
    {weakAreaDimensions.length > 0 && (
     <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
       <AlertTriangle className="w-5 h-5 text-orange-500" />
       Weak Areas
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
       These dimensions have an average score below 50% across your completed problems. Click to see practice recommendations.
      </p>
      <div className="flex flex-wrap gap-2">
       {weakAreaDimensions.map((dimension) => {
        const weakArea = analytics.weakAreas.find((w) => w.dimension === dimension);
        const percentage = weakArea ? Math.round(weakArea.averageScore * 10) : 0;

        return (
         <button
          key={dimension}
          onClick={() => handleWeakAreaClick(dimension)}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${selectedWeakArea === dimension
            ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300'
            : 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/30'
           }`}
         >
          <AlertTriangle className="w-4 h-4" />
          {DIMENSION_LABELS[dimension] || dimension}
          <span className="text-xs opacity-75">({percentage}%)</span>
         </button>
        );
       })}
      </div>

      {/* Recommendations Panel */}
      {selectedWeakArea && (
       <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
         <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Recommended Problems for {DIMENSION_LABELS[selectedWeakArea] || selectedWeakArea}
         </h3>
         <button
          onClick={() => {
           setSelectedWeakArea(null);
           setRecommendations([]);
          }}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
         >
          <X className="w-4 h-4" />
         </button>
        </div>

        {loadingRecommendations ? (
         <div className="flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Loading recommendations...</span>
         </div>
        ) : recommendations.length === 0 ? (
         <p className="text-sm text-gray-600 dark:text-gray-400 py-2">
          No recommendations available for this dimension.
         </p>
        ) : (
         <div className="space-y-2">
          {recommendations.map((rec) => (
           <Link
            key={rec.problemId}
            to={`/practice/lld/${rec.problemId}/mode`}
            className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-sm transition-all group"
           >
            <div className="flex-1 min-w-0">
             <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors truncate">
              {rec.title}
             </p>
             <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {rec.reason}
             </p>
            </div>
            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
             <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              {rec.difficulty}
             </span>
             <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors" />
            </div>
           </Link>
          ))}
         </div>
        )}
       </div>
      )}
     </div>
    )}

    {/* Recent Evaluations */}
    {analytics.recentEvaluations && analytics.recentEvaluations.length > 0 && (
     <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
       <CheckCircle className="w-5 h-5 text-green-500" />
       Recent Evaluations
      </h2>
      <div className="space-y-3">
       {analytics.recentEvaluations.slice(0, 5).map((evaluation) => {
        const percentage = Math.round(evaluation.overallScore * 10);
        return (
         <div
          key={`${evaluation.problemId}-${evaluation.submittedAt}`}
          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
         >
          <div className="flex-1 min-w-0">
           <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {evaluation.title}
           </p>
           <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            {new Date(evaluation.submittedAt).toLocaleDateString()}
           </p>
          </div>
          <div className={`text-sm font-semibold ${getScoreTextColor(percentage)}`}>
           {percentage}%
          </div>
         </div>
        );
       })}
      </div>
     </div>
    )}
   </div>
  </div>
 );
};

export default AnalyticsDashboard;
